import './Card.css';
import type { ComponentChildren, JSX } from 'preact';

/** Variantes de §3.8: `grow` ocupa a altura que sobra na coluna; `shrink` é recortado pelo `ScrollArea`; `oops` é o
 * corpo do `OopsCard` (§12.8). */
export type CardVariant = 'grow' | 'shrink' | 'oops';

type SectionAttrs = Omit<
  JSX.HTMLAttributes<HTMLElement>,
  'class' | 'className' | 'children' | 'aria-label' | 'aria-labelledby' | 'label'
>;

interface CardBaseProps extends SectionAttrs {
  variant?: CardVariant;
  /** Classe do dono (ex.: "hero-card"), somada a "card". O dono também pode trocar o `data-testid`. */
  class?: string;
  children?: ComponentChildren;
}

/**
 * AC-3.8-01: todo cartão tem nome acessível. Ou `labelledBy` aponta para o título do `CardHeader` (`titleId`), ou
 * `label` vira `aria-label` (cartão sem título visível). O tipo exige um dos dois.
 */
export type CardProps = CardBaseProps &
  ({ labelledBy: string; label?: undefined } | { label: string; labelledBy?: undefined });

/**
 * Cartão de vidro (§3.8 `Card`). A receita `.card` de base.css (§3.5.5) dá vidro, borda, raio, padding, a entrada
 * `kf-enter` (A01) e o +70 ms entre irmãos; os atrasos da intro (A02) chegam pelo `style` do dono.
 */
export function Card({ variant, class: extra, labelledBy, label, children, ...rest }: CardProps) {
  const classes = ['card'];
  if (variant) classes.push(`card--${variant}`);
  if (extra) classes.push(extra);
  return (
    <section
      data-testid="card"
      {...rest}
      class={classes.join(' ')}
      aria-labelledby={labelledBy || undefined}
      aria-label={labelledBy ? undefined : label || undefined}
    >
      {children}
    </section>
  );
}
